/**
 * Screen Wake Lock — keeps the display from dimming/locking during an active
 * round or penalty/try-on stage (losing the screen mid-round kills the camera
 * feed on most phones). Feature-detected (Safari 16.4+, most modern Chromium/
 * Android browsers); a silent no-op everywhere else. No UI of its own.
 *
 * The OS releases any held lock automatically whenever the tab goes hidden —
 * callers are expected to re-request on a visibilitychange back to visible
 * via `reacquireWakeLockIfWanted()` if they're still in an active context.
 */

const supported = typeof navigator !== "undefined" && "wakeLock" in navigator;

let sentinel: WakeLockSentinel | null = null;
/** True whenever the caller currently wants the lock held (independent of
 *  whether we actually hold one right now — the OS can drop it any time). */
let wanted = false;

export async function acquireWakeLock(): Promise<void> {
  wanted = true;
  if (!supported || sentinel) return;
  try {
    const s = await navigator.wakeLock.request("screen");
    if (!wanted) {
      // releaseWakeLock() ran while the request was in flight — don't adopt it.
      void s.release().catch(() => {});
      return;
    }
    sentinel = s;
    sentinel.addEventListener("release", () => {
      if (sentinel === s) sentinel = null;
    });
  } catch (err) {
    // NotAllowedError (backgrounded/hidden document, no user activation yet,
    // battery saver, etc.) is expected and non-fatal — the game just runs
    // without the lock until a later request succeeds.
    console.warn("[wakelock] request failed:", err);
  }
}

export function releaseWakeLock(): void {
  wanted = false;
  const s = sentinel;
  sentinel = null;
  if (s) void s.release().catch(() => {});
}

/** Call from a visibilitychange→visible handler. */
export function reacquireWakeLockIfWanted(): void {
  if (wanted && !sentinel) void acquireWakeLock();
}
